// Localização: frontend/src/components/Financeiro/ModalRecebimentoLote.jsx
// Modal para registrar recebimento em lote de múltiplas contas

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import { useContasBancarias } from '../../hooks/useFinanceiro';
import { DollarSign, Calendar, CreditCard } from 'lucide-react';

const ModalRecebimentoLote = ({ isOpen, onClose, contas, onReceber }) => {
    const [formData, setFormData] = useState({
        data_recebimento: '',
        conta_bancaria_id: ''
    });
    const [errors, setErrors] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const hasLoadedContasRef = useRef(false);

    // Hook para buscar contas bancárias
    const { contas: contasBancarias, fetchContas, loading: loadingContas } = useContasBancarias();

    useEffect(() => {
        if (isOpen) {
            // Carregar contas apenas uma vez quando o modal abrir
            if (!hasLoadedContasRef.current || contasBancarias.length === 0) {
                fetchContas().catch(err => {
                    console.error('[ModalRecebimentoLote] Erro ao carregar contas bancárias:', err);
                });
                hasLoadedContasRef.current = true;
            }

            const hoje = new Date().toISOString().split('T')[0];
            setFormData({
                data_recebimento: hoje,
                conta_bancaria_id: ''
            });
            setErrors({});
        } else {
            // Resetar flag quando o modal fechar
            hasLoadedContasRef.current = false;
        }
    }, [isOpen, fetchContas]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value || 0);
    };

    // Calcular valor total das contas selecionadas
    const calcularValorTotal = () => {
        return contas.reduce((total, conta) => {
            return total + parseFloat(conta.valor_pendente || 0);
        }, 0);
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.data_recebimento) {
            newErrors.data_recebimento = 'Data de recebimento é obrigatória';
        }

        if (!formData.conta_bancaria_id) {
            newErrors.conta_bancaria_id = 'Conta bancária é obrigatória';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsProcessing(true);
        try {
            const data = {
                data_recebimento: formData.data_recebimento,
                conta_bancaria_id: formData.conta_bancaria_id,
                juros: 0,
                multa: 0
            };

            // Processar recebimento de cada conta
            const resultado = await onReceber(contas, data);
            
            // Se houver erros, mostrar aviso mas não bloquear o fechamento
            if (resultado && resultado.erros && resultado.erros.length > 0) {
                console.warn('Algumas contas não puderam ser recebidas:', resultado.erros);
            }
            
            onClose();
        } catch (error) {
            console.error('Erro ao registrar recebimento em lote:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    if (!contas || contas.length === 0) return null;

    const valorTotal = calcularValorTotal();

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Receber Múltiplas Contas" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informações do Lote */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <h3 className="font-semibold text-emerald-900 mb-3">Resumo do Recebimento em Lote</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-emerald-700">Quantidade de Contas</p>
                            <p className="font-semibold text-emerald-900 text-lg">{contas.length}</p>
                        </div>
                        <div>
                            <p className="text-emerald-700">Valor Total</p>
                            <p className="font-semibold text-emerald-900 text-lg">{formatCurrency(valorTotal)}</p>
                        </div>
                    </div>
                </div>

                {/* Lista de Contas Selecionadas */}
                <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <h4 className="font-medium text-gray-900 mb-2 text-sm">Contas Selecionadas:</h4>
                    <div className="space-y-2">
                        {contas.map((conta) => (
                            <div key={conta.id} className="flex justify-between items-center text-sm bg-white p-2 rounded border border-gray-200">
                                <span className="text-gray-700">{conta.descricao}</span>
                                <span className="font-semibold text-emerald-600">{formatCurrency(conta.valor_pendente)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Data de Recebimento */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="inline h-4 w-4 mr-1" />
                        Data de Recebimento <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        name="data_recebimento"
                        value={formData.data_recebimento}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                            errors.data_recebimento ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.data_recebimento && (
                        <p className="mt-1 text-sm text-red-600">{errors.data_recebimento}</p>
                    )}
                </div>

                {/* Forma de Recebimento (Conta Bancária) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <CreditCard className="inline h-4 w-4 mr-1" />
                        Conta Bancária <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <select
                            name="conta_bancaria_id"
                            value={formData.conta_bancaria_id}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                errors.conta_bancaria_id ? 'border-red-500' : 'border-gray-300'
                            }`}
                            disabled={loadingContas}
                        >
                            <option value="">Selecione uma conta bancária...</option>
                            {contasBancarias.map(contaBancaria => (
                                <option key={contaBancaria.id} value={contaBancaria.id}>
                                    {contaBancaria.nome} - {contaBancaria.banco} {contaBancaria.agencia ? `(${contaBancaria.agencia})` : ''}
                                </option>
                            ))}
                        </select>
                        {loadingContas && (
                            <div className="absolute right-3 top-2.5">
                                <LoadingIndicator size="sm" />
                            </div>
                        )}
                    </div>
                    {errors.conta_bancaria_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.conta_bancaria_id}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                        Todas as contas serão recebidas na conta bancária selecionada
                    </p>
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        disabled={isProcessing}
                    >
                        Cancelar
                    </button>
                    <PrimaryButton 
                        type="submit" 
                        className="px-4 py-2 flex items-center gap-2"
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <>
                                <LoadingIndicator size="sm" />
                                Processando...
                            </>
                        ) : (
                            <>
                                <DollarSign className="h-4 w-4" />
                                Receber {contas.length} {contas.length === 1 ? 'Conta' : 'Contas'}
                            </>
                        )}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default ModalRecebimentoLote;

